import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { BellRing, CalendarDays, Check, Copy, History, MessageCircle, RotateCcw, ShieldCheck, XCircle } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDateTime, rupiah } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ChatPanel } from "../components/ChatPanel";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import PinConfirm from "../components/PinConfirm";
import { DatePicker } from "../components/DatePicker";

export default function BalanceManager() {
  const { user: me } = useAuth();
  const isAdmin = ["admin", "admin_fasnet"].includes(me?.role);
  const [sp] = useSearchParams();
  const focusUser = sp.get("user_id") || "";
  const focusAt = sp.get("at") || "";
  const [logs, setLogs] = useState([]);
  const [showAllLogs, setShowAllLogs] = useState(!!focusUser);
  const [filterFrom, setFilterFrom] = useState(sp.get("date") || "");
  const [filterTo, setFilterTo] = useState(sp.get("date") || "");
  const [filterMonth, setFilterMonth] = useState("");
  const [requests, setRequests] = useState([]);
  const [approveTarget, setApproveTarget] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [chatTarget, setChatTarget] = useState(null);

  const load = (from = filterFrom, to = filterTo, month = filterMonth) => {
    const params = {};
    if (from || to) {
      if (from) params.date_from = from;
      if (to) params.date_to = to;
    } else if (month) params.month = month;
    if (focusUser) params.user_id = focusUser;
    const filtered = !!(from || to || month || focusUser);
    api.get("/balance/logs", { params }).then((r) => setLogs(filtered ? r.data : r.data.slice(0, 40))).catch(() => {});
    api.get("/balance/topup-requests").then((r) => setRequests(r.data)).catch(() => {});
  };
  useEffect(() => { if (isAdmin) load(); }, [isAdmin]);

  const pendingReqs = requests.filter((r) => r.status === "pending");

  const doApprove = async () => {
    try {
      const { data } = await api.post(`/balance/topup-requests/${approveTarget.id}/approve`);
      toast.success(`Top-up ${approveTarget.email} disetujui — saldo kini ${rupiah(data.balance)}`);
      setApproveTarget(null); load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const doReject = async () => {
    try {
      await api.post(`/balance/topup-requests/${rejectTarget.id}/reject`);
      toast.success(`Permintaan top-up ${rejectTarget.email} ditolak`);
      setRejectTarget(null); load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  if (!isAdmin) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-3" data-testid="balance-forbidden">
        <ShieldCheck className="w-10 h-10 text-rose-500" />
        <p className="text-muted-foreground text-sm">Menu ini hanya untuk Super Admin & Admin Fasnet.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="balance-page">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Request Deposit</h1>
        <p className="text-sm text-muted-foreground mt-1">Setujui atau tolak request deposit agen. Setiap request mendapat kode unik 3 digit untuk memudahkan cek mutasi transfer.</p>
      </div>

      <Card className="bg-card border-border shadow-sm overflow-x-auto" data-testid="topup-requests-card">
        <CardHeader>
          <CardTitle className="text-base font-heading flex items-center gap-2">
            <BellRing className="w-4 h-4 text-amber-500" /> Antrian Request Deposit
            {pendingReqs.length > 0 && <span className="px-2 py-0.5 rounded-full text-xs font-mono bg-amber-500/10 text-amber-600 border border-amber-500/20">{pendingReqs.length} pending</span>}
          </CardTitle>
        </CardHeader>
        <Table className="min-w-[760px]" data-testid="topup-requests-table">
          <TableHeader>
            <TableRow>
              <TableHead>Email</TableHead><TableHead>Nama / Waktu</TableHead><TableHead>Bank</TableHead>
              <TableHead className="text-right">Nominal Transfer</TableHead><TableHead className="text-right">Aksi</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {!pendingReqs.length && (
              <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground" data-testid="topup-queue-empty">
                Tidak ada antrian — semua request deposit sudah diproses
              </TableCell></TableRow>
            )}
            {pendingReqs.map((r) => (
              <TableRow key={r.id} data-testid={`topup-req-${r.id}`}>
                <TableCell className="font-mono text-xs">{r.email}</TableCell>
                <TableCell className="font-medium">
                  {r.name || "-"}
                  <span className="block text-[10px] font-mono text-muted-foreground">{fmtDateTime(r.created_at)} • minta {rupiah(r.amount)} + kode unik <span className="text-amber-600 font-bold">{String(r.unique_code).padStart(3, "0")}</span></span>
                </TableCell>
                <TableCell>
                  <span className="px-2.5 py-0.5 rounded-full text-xs font-mono border bg-sky-500/10 text-sky-600 border-sky-500/20" data-testid={`topup-req-bank-${r.id}`}>{r.bank || "-"}</span>
                </TableCell>
                <TableCell className="text-right">
                  <div className="inline-flex items-center gap-1.5">
                    <span className="font-mono font-extrabold text-lg text-amber-600" data-testid={`topup-req-total-${r.id}`}>{rupiah(r.total_transfer)}</span>
                    <button type="button" title="Salin nominal"
                      onClick={async () => { try { await navigator.clipboard.writeText(String(r.total_transfer)); toast.success(`Nominal ${rupiah(r.total_transfer)} disalin`); } catch { toast.error("Gagal menyalin"); } }}
                      className="text-amber-600 hover:text-amber-500 transition-colors"
                      data-testid={`btn-copy-total-${r.id}`}>
                      <Copy className="w-4 h-4" />
                    </button>
                  </div>
                </TableCell>
                <TableCell className="text-right space-x-1 whitespace-nowrap">
                  <Button size="sm" variant="outline" className="text-sky-500" onClick={() => setChatTarget(r)} title="Chat agen: tanyakan jadi/tidaknya transfer" data-testid={`btn-chat-topup-${r.id}`}>
                    <MessageCircle className="w-3.5 h-3.5 mr-1" /> Chat
                  </Button>
                  <Button size="sm" onClick={() => setApproveTarget(r)} data-testid={`btn-approve-topup-${r.id}`}>
                    <Check className="w-3.5 h-3.5 mr-1" /> Setujui
                  </Button>
                  <Button size="sm" variant="outline" className="text-rose-500" onClick={() => setRejectTarget(r)} data-testid={`btn-reject-topup-${r.id}`}>
                    <XCircle className="w-3.5 h-3.5 mr-1" /> Tolak
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Card className="bg-card border-border shadow-sm overflow-x-auto" data-testid="balance-log-card">
        <CardHeader><CardTitle className="text-base font-heading flex items-center gap-2"><History className="w-4 h-4 text-sky-500" /> Riwayat Mutasi Saldo</CardTitle></CardHeader>
        <div className="px-4 sm:px-6 pb-3 flex items-center gap-2 flex-wrap" data-testid="balance-log-filters">
          <CalendarDays className="w-4 h-4 text-sky-500" />
          <span className="text-xs text-muted-foreground">Filter:</span>
          {focusUser && <span className="text-[11px] px-2 py-0.5 rounded border border-amber-500/40 bg-amber-500/10 text-amber-500" data-testid="balance-log-focus">Hanya mutasi 1 agen (dari bukti komisi){focusAt ? ` · sekitar ${fmtDateTime(focusAt)}` : ""} — baris yang cocok disorot</span>}
          <Input type="month" value={filterMonth} onChange={(e) => { setFilterMonth(e.target.value); setFilterFrom(""); setFilterTo(""); load("", "", e.target.value); }} className="w-40 h-8 text-xs" data-testid="filter-log-month" title="Filter per bulan" />
          <span className="text-xs text-muted-foreground">Dari</span>
          <DatePicker value={filterFrom} onChange={(v) => { setFilterFrom(v); setFilterMonth(""); load(v, filterTo, ""); }} className="w-36 h-8 text-xs" testid="filter-log-from" placeholder="Dari tanggal" />
          <span className="text-xs text-muted-foreground">Sampai</span>
          <DatePicker value={filterTo} onChange={(v) => { setFilterTo(v); setFilterMonth(""); load(filterFrom, v, ""); }} className="w-36 h-8 text-xs" testid="filter-log-to" placeholder="Sampai tanggal" />
          {(filterFrom || filterTo || filterMonth) && (
            <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={() => { setFilterFrom(""); setFilterTo(""); setFilterMonth(""); load("", "", ""); }} data-testid="btn-log-filter-reset">
              <RotateCcw className="w-3.5 h-3.5 mr-1" /> Reset
            </Button>
          )}
        </div>
        <Table className="min-w-[520px]" data-testid="balance-logs">
          <TableHeader>
            <TableRow>
              <TableHead>Keterangan</TableHead>
              <TableHead className="text-right">Masuk</TableHead>
              <TableHead className="text-right">Keluar</TableHead>
              <TableHead className="text-right">Saldo Akhir</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {!logs.length && <TableRow><TableCell colSpan={4} className="text-center py-8 text-muted-foreground">{(filterFrom || filterTo || filterMonth) ? "Tidak ada mutasi pada periode ini" : "Belum ada mutasi saldo"}</TableCell></TableRow>}
            {(showAllLogs ? logs : logs.slice(0, 5)).map((l) => (
              <TableRow key={l.id} data-testid={`balance-log-${l.id}`} className={focusAt && Math.abs(new Date(l.created_at) - new Date(focusAt)) <= 180000 ? "bg-amber-500/15" : ""}>
                <TableCell className="py-1.5">
                  <div className="text-xs font-medium break-words leading-snug" data-testid={`log-agent-${l.id}`}>{l.agent_label || l.email} — {l.note}</div>
                  <div className="text-[10px] text-muted-foreground font-mono flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
                    <span data-testid={`log-by-${l.id}`}>{fmtDateTime(l.created_at)} • oleh {l.by_label || "sistem"}</span>
                    {l.unique_code ? <span className="px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-600 border border-amber-500/20">kode unik {String(l.unique_code).padStart(3, "0")}</span> : null}
                  </div>
                </TableCell>
                <TableCell className="text-right font-mono font-semibold text-emerald-600 text-xs whitespace-nowrap py-1.5">{l.amount > 0 ? `+${rupiah(l.amount)}` : "-"}{l.pct && <span className="ml-1 px-1 py-0.5 rounded bg-emerald-500/15 border border-emerald-500/30 text-[9px] font-semibold" data-testid={`log-pct-${l.id}`}>{l.pct}</span>}</TableCell>
                <TableCell className="text-right font-mono font-semibold text-rose-500 text-xs whitespace-nowrap py-1.5">{l.amount < 0 ? `-${rupiah(Math.abs(l.amount))}` : "-"}</TableCell>
                <TableCell className="text-right font-mono text-xs whitespace-nowrap py-1.5" data-testid={`balance-after-${l.id}`}>{l.balance_after != null ? rupiah(l.balance_after) : "-"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {logs.length > 5 && (
          <div className="border-t border-border">
            <Button variant="ghost" size="sm" className="w-full text-xs" onClick={() => setShowAllLogs(!showAllLogs)} data-testid="btn-mutation-more">
              {showAllLogs ? "Tampilkan lebih sedikit" : `Selengkapnya (${logs.length - 5} mutasi lainnya)`}
            </Button>
          </div>
        )}
      </Card>

      <PinConfirm
        open={!!approveTarget}
        onOpenChange={() => setApproveTarget(null)}
        title="Setujui Top-up?"
        description={approveTarget ? `Saldo ${approveTarget.email} akan ditambah = nominal ${rupiah(approveTarget.amount)} + kode unik ${String(approveTarget.unique_code).padStart(3, "0")}. Transfer masuk ke bank ${approveTarget.bank || "-"} — cek mutasi bank tersebut.` : ""}
        highlight={approveTarget ? rupiah(approveTarget.total_transfer) : ""}
        copyValue={approveTarget ? String(approveTarget.total_transfer) : ""}
        confirmLabel="Ya, Setujui & Tambah Saldo"
        onConfirm={doApprove}
        testid="pin-approve-topup"
      />
      <PinConfirm
        open={!!rejectTarget}
        onOpenChange={() => setRejectTarget(null)}
        title="Tolak Permintaan Top-up?"
        description={rejectTarget ? `Permintaan ${rejectTarget.email} sebesar ${rupiah(rejectTarget.total_transfer)} akan ditolak.` : ""}
        confirmLabel="Ya, Tolak"
        destructive
        onConfirm={doReject}
        testid="pin-reject-topup"
      />
      <Dialog open={!!chatTarget} onOpenChange={(v) => { if (!v) setChatTarget(null); }}>
        <DialogContent className="max-w-lg p-0 overflow-hidden" data-testid="dialog-topup-chat">
          <DialogHeader className="px-4 pt-4">
            <DialogTitle className="text-base font-heading flex items-center gap-2"><MessageCircle className="w-4 h-4 text-sky-500" /> Chat {chatTarget?.name || chatTarget?.email}</DialogTitle>
            <DialogDescription className="text-xs">Request {chatTarget ? rupiah(chatTarget.total_transfer) : ""} via {chatTarget?.bank || "-"} — tanyakan apakah transfer sudah/akan dilakukan. Agen melihat chat ini di halaman Isi Saldo.</DialogDescription>
          </DialogHeader>
          <div className="px-4 pb-4">
            {chatTarget && <ChatPanel channel={`agent:${chatTarget.user_id}`} testid="topup-admin-chat" compact />}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
